/**
 * Раздел «Доступ» — кому открыто приложение в закрытой установке
 * (ACCESS_MODE=admins, портал «Чистый Лист», решение 25.09.2026).
 *
 * Администраторам портала доступ открыт всегда, и в списке их нет: это право
 * определяет сам портал. Здесь администратор открывает приложение остальным
 * сотрудникам и закрывает его. Закрытие действует сразу: сервер проверяет
 * список при каждом запросе.
 */

import { useEffect, useState } from 'react';
import { api, type AccessGrant } from '../../api.js';
import { selectUsers } from '../../bx24.js';

export function AccessPage() {
  const [grants, setGrants] = useState<AccessGrant[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.access
      .list()
      .then((r) => setGrants(r.grants))
      .catch((e) => setMessage((e as Error).message));
  }, []);

  const add = async () => {
    const users = await selectUsers();
    if (!users.length) return;
    setBusy(true);
    try {
      const r = await api.access.grant(users);
      setGrants(r.grants);
      setMessage(`Доступ открыт: ${users.map((u) => u.name).join(', ')}`);
    } catch (error) {
      setMessage((error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (grant: AccessGrant) => {
    if (!confirm(`Закрыть доступ к приложению: ${grant.userName || grant.userId}?`)) return;
    try {
      const r = await api.access.revoke(grant.userId);
      setGrants(r.grants);
      setMessage(`Доступ закрыт: ${grant.userName || grant.userId}`);
    } catch (error) {
      setMessage((error as Error).message);
    }
  };

  return (
    <div className="block">
      <h2>Доступ к приложению</h2>
      <p className="note">
        Приложение открыто администраторам портала. Остальным сотрудникам доступ открывается здесь — они смогут работать
        с делами, но не менять справочники и шаблоны.
      </p>

      <div className="toolbar">
        <button type="button" className="primary" disabled={busy} onClick={() => void add()}>
          Открыть доступ сотрудникам
        </button>
        {message && <span className="hint">{message}</span>}
      </div>

      {grants.length === 0 ? (
        <p className="hint">Пока доступ открыт только администраторам портала.</p>
      ) : (
        <table className="simple-table">
          <thead>
            <tr>
              <th>Сотрудник</th>
              <th>Доступ открыт</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {grants.map((grant) => (
              <tr key={grant.userId}>
                <td>{grant.userName || `Сотрудник ${grant.userId}`}</td>
                <td>{new Date(grant.grantedAt).toLocaleDateString('ru-RU')}</td>
                <td>
                  <button type="button" className="link-button" onClick={() => void revoke(grant)}>
                    Закрыть доступ
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
